/**
 * RAM disclosure-trigger emit helper (P1 fix 2026-04-27).
 *
 * 5 RAM actions have a regulatory disclosure-redelivery requirement on
 * commit. The full server-side post-commit pipeline (mint a SignNow
 * envelope + return `disclosureSession.signingGroupId` so the dialog can
 * navigate to /rsc) is a Wave A follow-up per the integration design plan.
 *
 * In the meantime this helper emits a structured SessionEvent so:
 *   1. The audit trail records that a disclosure SHOULD be redelivered
 *      (regulatory traceability — "the system knew it owed the customer
 *      this notice").
 *   2. A Timer-trigger backend sweep (Wave A) can pick up these events and
 *      mint the SignNow sessions retroactively without the dialog blocking.
 *   3. A Compliance UI can surface "pending redelivery" backlog for review.
 *
 * Action → required template(s):
 *   change-statement-prefs (when activating eStatements) → ESIGN
 *   change-od-protection                                → ODP_A9
 *   change-title                                        → SIG_CARD
 *   change-ownership                                    → SIG_CARD + W9
 *   change-rate-index (CDs)                             → RATE_SCHED + CD_TIS
 */

import type { ApiClient } from '@retail-ops/api-client';

export type RamDisclosureActionCode =
  | 'change-statement-prefs'
  | 'change-od-protection'
  | 'change-title'
  | 'change-ownership'
  | 'change-rate-index';

export interface RamDisclosureTriggerInput {
  action: RamDisclosureActionCode;
  cifId: string;
  accountNumber?: string;
  /** Free-form payload describing what changed — captured for audit + Wave-A
   *  backend to use as IdempotencyHash input. */
  payload?: Record<string, unknown>;
  /** FSR identity for the SessionEvent.actor field. */
  actorUpn?: string;
}

/** Per-action regulatory citation + required template list. Static map; the
 *  Wave-A backend resolver will eventually authoritatively compute this from
 *  customer-type + payload but for the audit-trail emit we hardcode the
 *  canonical map to keep dialogs simple. */
const TRIGGER_MAP: Record<
  RamDisclosureActionCode,
  { templates: ReadonlyArray<string>; reg: string; predicate?: string }
> = {
  'change-statement-prefs':  { templates: ['ESIGN'],                 reg: 'REG_E_1005_10', predicate: 'eStatement === true' },
  'change-od-protection':    { templates: ['ODP_A9'],                reg: 'REG_E_1005_17_A9' },
  'change-title':            { templates: ['SIG_CARD'],              reg: 'REG_DD_1030_4' },
  'change-ownership':        { templates: ['SIG_CARD', 'W9'],        reg: 'REG_DD_1030_4 + IRS_W9' },
  'change-rate-index':       { templates: ['RATE_SCHED', 'CD_TIS'],  reg: 'REG_DD_1030_5' },
};

/**
 * Fire a `disclosure-redelivery-required` SessionEvent. Best-effort — never
 * throws (failure here must not break the parent commit). The Wave-A backend
 * sweep will retry from this event as the source of truth.
 */
export async function emitDisclosureRedeliveryRequired(
  client: ApiClient,
  input: RamDisclosureTriggerInput,
): Promise<void> {
  const trigger = TRIGGER_MAP[input.action];
  if (!trigger) return;

  await client.retail.events
    .write({
      eventType: 'disclosure-redelivery-required',
      eventCategory: 'compliance',
      severity: 'warn',
      actor: input.actorUpn ?? 'unknown',
      payloadJson: JSON.stringify({
        action: input.action,
        cifId: input.cifId,
        accountNumber: input.accountNumber,
        templates: trigger.templates,
        triggerReg: trigger.reg,
        ...(trigger.predicate ? { predicate: trigger.predicate } : {}),
        actionPayload: input.payload ?? {},
        // Idempotency hint for the Wave-A sweeper — same shape repeated should
        // produce the same hash and suppress duplicate redeliveries.
        idempotencyKey:
          `${input.action}|${input.cifId}|${input.accountNumber ?? ''}|` +
          trigger.templates.join(','),
      }),
    })
    .catch(() => undefined);
}
